import { useState, useEffect, useContext } from "react";
import placeholder from "../inc/placeholder.jpg";
import { SiteContext } from "../contexts/SiteContext";
import { useAuth } from "../admin/contexts/AuthContext";
import fetchSite from "../utils/fetchSite";
import { useParams, Link } from "react-router-dom";
import {
  Paper,
  Typography,
  makeStyles,
  Grid,
  Fade,
  Divider,
  List,
  ListItem,
  Chip,
} from "@material-ui/core";
import DescriptionIcon from "@material-ui/icons/Description";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import Warning from "../components/Warning";
import NotFound from "../components/NotFound";
import AdminBar from "../components/AdminBar";
import NotAllowed from "../components/NotAllowed";
import RelatedPeople from "../components/RelatedPeople";
import RelatedEntries from "../components/RelatedEntries";
import Banner from "../components/Banner";

function Entry() {
  const { isAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState();
  const { selectedSite, setSelectedSite } = useContext(SiteContext);
  const { siteSlug, entrySlug } = useParams();

  const useStyles = makeStyles((theme) => ({
    root: {
      flex: 1,
    },
    main: {
      margin: "0 auto",
      padding: "1rem",
      paddingTop: 0,
      maxWidth: process.env.REACT_APP_MAX_WIDTH,
    },
    paper: {
      padding: "1rem",
    },
    downloads: {
      "& a": {
        textDecoration: "none",
      },
    },
    themes: {
      listStyle: "none",
      display: "flex",
      justifyContent: "center",
      flexWrap: "wrap",
      padding: theme.spacing(0.5),
      margin: 0,
    },
    regions: {
      listStyle: "none",
      display: "flex",
      justifyContent: "center",
      flexWrap: "wrap",
      padding: theme.spacing(0.5),
      margin: 0,
    },
    chip: {
      cursor: "pointer",
    },
    title: {
      color:
        selectedSite && selectedSite.secondaryColor
          ? selectedSite.secondaryColor
          : theme.palette.secondary.main,
    },
    featuredImage: {
      width: "100%",
    },
    button: {
      fontWeight: "bold",
      fontSize: ".7rem",
    },
    link: {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
  }));

  const classes = useStyles();

  // This effect sets the selectedSite based on the :siteSlug (first url parameter)
  useEffect(() => {
    fetchSite(siteSlug, setSelectedSite);
  }, [siteSlug, setSelectedSite]);

  // This effect sets the entry and uses the selectedSite._id to make sure it's on the right site
  useEffect(() => {
    async function fetchEntry() {
      let res = await fetch(
        `${process.env.REACT_APP_API_URL}/entries?unit.site=${selectedSite._id}`,
        { credentials: "include" }
      );
      res = await res.json();
      if (res.success) {
        res.data = res.data.filter((entry) => entry.slug === entrySlug);
        setEntry(res.data[0]);
        setLoading(false);
      } else {
        console.error(res.error);
      }
    }
    if (selectedSite) {
      fetchEntry();
    }
  }, [selectedSite, entrySlug]);

  if (loading || !selectedSite) {
    return <Loading />;
  }

  if (!loading && !entry) {
    return <NotFound />;
  }

  // If the resource/site isn't published and the user isn't logged in, don't show the page
  if (!isAuth && (!entry.published || !selectedSite.published)) {
    return <NotAllowed />;
  }

  return (
    <Fade in={typeof entry != "undefined"}>
      <div className={classes.root}>
        <AdminBar />
        <Header />
        <AppBar />
        <Banner />
        <Breadcrumbs
          links={[
            {
              name: "Entries",
              url: `/${selectedSite.slug}/entries`,
            },
            {
              name: entry.title,
              url: `/${selectedSite.slug}/entries/${entry.slug}`,
            },
          ]}
        />
        <main className={classes.main}>
          <Grid container spacing={3}>
            <Grid item sm={12} md={4}>
              <Paper style={{ marginBottom: "1rem" }} className={classes.paper}>
                <img
                  className={classes.featuredImage}
                  src={
                    entry.featuredImage
                      ? `${process.env.REACT_APP_FILE_URL}/${entry.featuredImage.fileName}`
                      : //: "https://source.unsplash.com/random?collection=1995427&size=500x500"
                      placeholder
                  }
                  alt={`${entry.name} featured`}
                />
                {
                  entry.themes && entry.themes.length > 0 &&
                  <>
                    <h3 style={{ textAlign: 'center' }}>Themes:</h3>
                    <ul className={classes.themes}>
                      {entry.themes.map((theme, i) => (
                        <li key={i}>
                          <Chip
                            component={Link}
                            to={`/${siteSlug}/themes/${theme.slug}`}
                            label={theme.name}
                            className={classes.chip}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                }
                {
                  entry.regions && entry.regions.length > 0 &&
                  <>
                    <h3 style={{ textAlign: 'center' }}>Regions:</h3>
                    <ul className={classes.regions}>
                      {entry.regions.map((region, i) => (
                        <li key={i}>
                          <Chip
                            component={Link}
                            to={`/${siteSlug}/regions/${region.slug}`}
                            label={region.name}
                            className={classes.chip}
                          />
                        </li>
                      ))}
                    </ul>
                  </>
                }
              </Paper>

              {(entry.hires || entry.transcription) &&
                ((entry.hires && entry.hires.fileName) ||
                  (entry.transcription && entry.transcription.fileName)) && (
                  <Paper
                    style={{ marginTop: "1rem" }}
                    className={classes.paper}
                  >
                    <Typography variant="h6" component="h3">
                      Downloads
                    </Typography>

                    <List className={classes.downloads}>
                      {entry.hires && entry.hires.fileName && (
                        <a
                          className={classes.link}
                          href={`${process.env.REACT_APP_FILE_URL}/${entry.hires.fileName}`}
                          download
                        >
                          <ListItem button>
                            Download in High Resolution
                          </ListItem>
                        </a>
                      )}

                      {entry.transcription && entry.transcription.fileName && (
                        <ListItem button>
                          <a
                            className={classes.link}
                            href={`${process.env.REACT_APP_FILE_URL}/${entry.transcription.fileName}`}
                            download
                          >
                            Download Transcription
                          </a>
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                )}
              <Warning margin="1rem 0" />
            </Grid>
            <Grid item sm={12} md={8}>
              <Paper className={classes.paper}>
                <Link
                  className={classes.link}
                  to={`/${siteSlug}/units/${entry.unit.slug}`}
                >
                  <Typography gutterBottom component="p">
                    From the Unit "<strong>{`${entry.unit.name}`}</strong>"
                  </Typography>
                </Link>
                <Typography
                  className={classes.title}
                  style={{ marginTop: "1rem" }}
                  gutterBottom
                  component="h2"
                  variant="h6"
                >
                  <DescriptionIcon /> Entry
                </Typography>
                <Typography
                  style={{ marginTop: "1rem" }}
                  gutterBottom
                  component="h2"
                  variant="h5"
                >
                  {entry.title}
                </Typography>
                <div
                  dangerouslySetInnerHTML={{ __html: entry.description }}
                ></div>
                <Divider variant="middle" />
                <Typography
                  style={{ marginTop: "1rem" }}
                  gutterBottom
                  component="h2"
                  variant="h6"
                >
                  Historical Background
                </Typography>
                <div
                  dangerouslySetInnerHTML={{
                    __html: entry.historicalBackground,
                  }}
                ></div>
                {entry.citationText && (
                  <>
                    <Divider variant="middle" />
                    <Typography
                      style={{ marginTop: "1rem" }}
                      gutterBottom
                      component="h2"
                      variant="h6"
                    >
                      Citation
                    </Typography>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: entry.citationText,
                      }}
                    ></div>
                  </>
                )}
                {entry.repository && (
                  <>
                    <Divider variant="middle" />
                    <Typography
                      style={{ marginTop: "1rem" }}
                      gutterBottom
                      component="h2"
                      variant="h6"
                    >
                      Repository
                    </Typography>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: entry.repository,
                      }}
                    ></div>
                  </>
                )}
                {entry.links && (
                  <>
                    <Divider variant="middle" />
                    <Typography
                      style={{ marginTop: "1rem" }}
                      gutterBottom
                      component="h2"
                      variant="h6"
                    >
                      Links
                    </Typography>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: entry.links,
                      }}
                    ></div>
                  </>
                )}
                {entry.forEducators && (
                  <>
                    <Divider variant="middle" />
                    <Typography
                      style={{ marginTop: "1rem" }}
                      gutterBottom
                      component="h2"
                      variant="h6"
                    >
                      For Educators
                    </Typography>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: entry.forEducators,
                      }}
                    ></div>
                  </>
                )}
                {entry.suggestedReading && (
                  <>
                    <Divider variant="middle" />
                    <Typography
                      style={{ marginTop: "1rem" }}
                      gutterBottom
                      component="h2"
                      variant="h6"
                    >
                      Suggested Reading
                    </Typography>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: entry.suggestedReading,
                      }}
                    ></div>
                  </>
                )}
                {entry.relatedPeople && entry.relatedPeople.length > 0 && (
                  <RelatedPeople people={entry.relatedPeople} />
                )}
                {entry.relatedEntries && entry.relatedEntries.length > 0 && (
                  <RelatedEntries entries={entry.relatedEntries} />
                )}
              </Paper>
            </Grid>
          </Grid>
        </main>
      </div>
    </Fade>
  );
}

export default Entry;
